import { forwardRef, useState } from 'react';
import type { FontValue } from '@elfsight/configurator-contracts';
import {
  Check16,
  Email,
  FacebookMulticolor,
  Link16,
  LinkedinMulticolor,
  TelegramMulticolor,
  TwitterXMulticolor,
  WhatsappMulticolor
} from '@elfsight/icons';
import { copyTextToClipboard } from '@elfsight/lib-utils';
import { openWindow } from '@elfsight/widget-utils';
import { useTranslation } from 'react-i18next';
import type { DropdownProps } from '../dropdown';
import { Dropdown, DropdownItem, DropdownSeparator } from '../dropdown';

export type ShareDropdownProps = Omit<DropdownProps, 'children'> & {
  link: string;
  font?: FontValue;
  onItemClick?: (destination: string) => void;
  shadow?: boolean;
};

const copyLinkLabel = 'Copy Link';

export const ShareDropdown = forwardRef<HTMLDivElement, ShareDropdownProps>(
  function ShareDropdown(
    {
      link,
      popoutDirection = 'upward',
      onCloseRequest,
      onItemClick,
      shadow,
      ...forwardingProps
    },
    forwardingRef
  ) {
    const { t } = useTranslation();
    const [linkCopied, setLinkCopied] = useState(false);

    const handleCopy = () => {
      onItemClick?.('link');
      setLinkCopied(true);
      copyTextToClipboard(link);

      setTimeout(() => {
        setLinkCopied(false);
        onCloseRequest();
      }, 1000);
    };

    const handleShare = (shareLink: string, destination: string) => {
      onItemClick?.(destination);
      openWindow(shareLink + link);
      onCloseRequest();
    };

    return (
      <Dropdown
        {...forwardingProps}
        ref={forwardingRef}
        closeOnClick={false}
        iconSize={20}
        popoutDirection={popoutDirection}
        onCloseRequest={onCloseRequest}
        shadow={shadow}
      >
        <DropdownItem
          icon={linkCopied ? Check16 : Link16}
          onClick={handleCopy}
          ariaLabel={t(copyLinkLabel)}
        >
          {/* // TODO: Обернуть фразу в lingui макрос после переноса social-feed в WU */}
          {'Link Copied'}
        </DropdownItem>

        <DropdownSeparator />

        {platformsMapping.map(({ label, destination, icon, shareLink }) => (
          <DropdownItem
            key={label}
            icon={icon}
            onClick={() => handleShare(shareLink, destination)}
            ariaLabel={t(label)}
          >
            {t(label)}
          </DropdownItem>
        ))}
      </Dropdown>
    );
  }
);

// TODO: Обернуть лейблы в lingui макросы после переноса social-feed в WU (сделать useGetPlaformsMapping)
const platformsMapping = [
  {
    label: 'Share on Facebook',
    destination: 'facebook',
    icon: FacebookMulticolor,
    shareLink: 'https://www.facebook.com/sharer/sharer.php?u='
  },
  {
    label: 'Share on WhatsApp',
    destination: 'whatsapp',
    icon: WhatsappMulticolor,
    shareLink: 'https://api.whatsapp.com/send?text='
  },
  {
    label: 'Share on Telegram',
    destination: 'telegram',
    icon: TelegramMulticolor,
    shareLink: 'https://telegram.me/share/url?url='
  },
  {
    label: 'Share via Email',
    destination: 'email',
    icon: Email,
    shareLink: 'mailto:?body='
  },
  {
    label: 'Share on X',
    destination: 'x',
    icon: TwitterXMulticolor,
    shareLink: 'https://twitter.com/intent/tweet?url='
  },
  {
    label: 'Share on LinkedIn',
    destination: 'linkedin',
    icon: LinkedinMulticolor,
    shareLink: 'https://www.linkedin.com/shareArticle?url='
  }
];
